import type React from "react";
import { useEffect, useRef, useState } from "react";
import { eventEmitter } from "../../../utils/eventEmitter";

// Mirrors the backend payload from
// superdoc/views.py:_maybe_emit_ehr_transfer_prompt.
export interface EhrTransferPromptData {
  audio_file_id: number;
  question: string;
}

type Resolution = "pending" | "started" | "declined" | "failed" | "completed";

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M14 2L7.5 8.5M14 2L9.5 14L7.5 8.5M14 2L2 6.5L7.5 8.5"
      stroke="#6366F1"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EhrTransferPromptWidget: React.FC<{ data: EhrTransferPromptData; disabled?: boolean }> = ({ data, disabled }) => {
  const [resolution, setResolution] = useState<Resolution>("pending");
  const [failureDetail, setFailureDetail] = useState("");
  const resolutionRef = useRef(resolution);
  resolutionRef.current = resolution;
  const audioFileId = data?.audio_file_id;

  // Track the transfer outcome so the card never lies about a transfer
  // that failed to start (e.g. missing patient DOB/MRN) or finished.
  // Only a card that initiated a transfer ("started") reacts — a still-
  // pending offer shouldn't flip because of a panel-initiated transfer.
  useEffect(() => {
    if (!audioFileId) return;
    const onFailed = (...args: unknown[]) => {
      const d = args[0] as { audioId?: number; error?: string };
      if (d?.audioId !== audioFileId || resolutionRef.current !== "started") return;
      setFailureDetail(d.error || "");
      setResolution("failed");
    };
    const onCompleted = (...args: unknown[]) => {
      const d = args[0] as { audioId?: number };
      if (d?.audioId !== audioFileId || resolutionRef.current !== "started") return;
      setResolution("completed");
    };
    eventEmitter.on("ehrTransferFailed", onFailed);
    eventEmitter.on("ehrTransferCompleted", onCompleted);
    return () => {
      eventEmitter.off("ehrTransferFailed", onFailed);
      eventEmitter.off("ehrTransferCompleted", onCompleted);
    };
  }, [audioFileId]);

  if (!data || !data.audio_file_id) return null;

  const handleTransfer = () => {
    if (resolution !== "pending" && resolution !== "failed") return;
    setFailureDetail("");
    setResolution("started");
    // Open the note in the right panel first — TransferProvider mounts
    // inside NotesView, so this guarantees a listener exists (or the
    // module-level pending store in TransferContext catches the race).
    eventEmitter.emit("artifactUpdated", { audio_file_id: data.audio_file_id, kinds: ["note"] });
    eventEmitter.emit("startEhrTransferRequested", { audioId: data.audio_file_id });
  };

  if (resolution === "started") {
    return (
      <div style={{ ...styles.container, ...styles.containerResolved }}>
        <div style={styles.header}>
          <SendIcon />
          <span style={styles.headerText}>Transfer started — watch the note panel for progress</span>
        </div>
      </div>
    );
  }
  if (resolution === "completed") {
    return (
      <div style={{ ...styles.container, ...styles.containerResolved }}>
        <div style={styles.header}>
          <SendIcon />
          <span style={styles.headerText}>Note transferred to your EHR</span>
        </div>
      </div>
    );
  }
  if (resolution === "failed") {
    return (
      <div style={{ ...styles.container, ...styles.containerFailed }}>
        <div style={styles.header}>
          <SendIcon />
          <span style={styles.headerText}>Transfer failed</span>
        </div>
        {failureDetail && <div style={styles.failureDetail}>{failureDetail}</div>}
        <div style={styles.actionsRow}>
          <button
            type="button"
            disabled={disabled}
            onClick={handleTransfer}
            style={{ ...styles.button, ...styles.dismissButton, ...(disabled ? styles.buttonDisabled : {}) }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (resolution === "declined") {
    return (
      <div style={{ ...styles.container, ...styles.containerResolved }}>
        <div style={styles.header}>
          <SendIcon />
          <span style={styles.headerText}>No problem — you can transfer from the note panel anytime</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <SendIcon />
        <span style={styles.headerText}>{data.question || "Transfer this note to your EHR?"}</span>
      </div>
      <div style={styles.actionsRow}>
        <button
          type="button"
          disabled={disabled}
          onClick={handleTransfer}
          style={{ ...styles.button, ...styles.confirmButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          Transfer to EHR
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setResolution("declined")}
          style={{ ...styles.button, ...styles.dismissButton, ...(disabled ? styles.buttonDisabled : {}) }}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  containerResolved: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  containerFailed: {
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
  },
  failureDetail: {
    fontSize: "13px",
    color: "#B91C1C",
    lineHeight: "1.4",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    marginTop: "4px",
  },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background-color 0.15s",
  },
  confirmButton: {
    backgroundColor: "#4338CA",
    color: "#FFFFFF",
    borderColor: "#4338CA",
  },
  dismissButton: {
    backgroundColor: "#FFFFFF",
    color: "#374151",
    borderColor: "#D1D5DB",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

export default EhrTransferPromptWidget;
